export interface CalculatorField {
  label: string;
  hint?: string;
  /**
   * The greyed default: shown as the field's placeholder and used by Calculate when the field is
   * untouched. One value drives both, so the number a user sees can never differ from the one
   * the calculation runs on.
   */
  placeholder?: string;
}

/**
 * Falls back to the field's greyed default so an untouched form still calculates. A field the user
 * typed in and then cleared is left empty on purpose, so validation still raises its error rather
 * than silently calculating on a number they deliberately removed.
 */
export const valueOrPlaceholder = (raw: string, field: CalculatorField, isTouched: boolean): string =>
  raw.trim() === '' && !isTouched ? (field.placeholder ?? '') : raw;

/** Raw field values keyed by label; parsed by each calculator's math module. */
export type CalculatorFieldValues = Record<string, string>;

/** Substitutes each untouched field's greyed default, so Calculate reads the numbers on screen. */
export const withPlaceholders = (
  values: CalculatorFieldValues,
  fields: readonly CalculatorField[],
  isTouched: (label: string) => boolean,
): CalculatorFieldValues => {
  const resolved = { ...values };

  fields.forEach(field => {
    resolved[field.label] = valueOrPlaceholder(values[field.label], field, isTouched(field.label));
  });

  return resolved;
};

/** Dropdown and radio options are closed sets with defaults, so a key that misses is a coding error, not user input. */
export const findOption = <O extends { key: string }>(options: readonly O[], key: O['key'], name: string): O => {
  const option = options.find(candidate => candidate.key === key);

  if (!option) throw new Error(`Unsupported ${name}: ${key}`);

  return option;
};

export const mapFieldValues = (
  values: CalculatorFieldValues,
  transform: (raw: string, label: string) => string,
): CalculatorFieldValues =>
  Object.fromEntries(Object.entries(values).map(([label, raw]) => [label, transform(raw, label)]));

/** Missing keys are valid fields. */
export type CalculatorFieldErrors = Partial<Record<string, string>>;
